import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart3 } from 'lucide-react';
import MailboxList from '../components/mailbox/MailboxList';
import EmailView from '../components/mailbox/EmailView';
import EmailCompose from '../components/mailbox/EmailCompose';

const Mailbox = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('booking'); // 'booking', 'ap'
    const [view, setView] = useState('list'); // 'list', 'view', 'compose', 'reply', 'reply-all'
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [replyTo, setReplyTo] = useState(null);
    const [isReplyAll, setIsReplyAll] = useState(false);
    const [currentFolder, setCurrentFolder] = useState('inbox');
    const [voiceComposeData, setVoiceComposeData] = useState(null);

    // Tab configurations (Quote Automation hidden)
    const tabs = [
        {
            id: 'booking',
            label: 'Booking Automation',
            email: 'infoxai@infoxwebware.net',
            password: 'Infox@335',
            dashboardUrl: 'https://docsapp.infoxsystem.com/Admin/MAintainBookingAutomation.aspx'
        },
        {
            id: 'ap',
            label: 'AP Automation',
            email: 'apautomation@infoxwebware.net',
            password: 'Infox@335',
            dashboardUrl: 'https://docsapp.infoxsystem.com/Admin/MAintainVendorInvoiceAudit.aspx'
        }
    ];

    // Handle voice commands from location state
    React.useEffect(() => {
        if (location.state?.voiceCommand) {
            const voiceCommand = location.state.voiceCommand;
            if (voiceCommand.action === 'compose') {
                setReplyTo(null);
                setIsReplyAll(false);
                setVoiceComposeData(voiceCommand.data);
                setView('compose');
            }
        }
    }, [location]);

    const handleEmailSelect = (email) => {
        setSelectedEmail(email);
        setView('view');
    };

    const handleCompose = () => {
        setReplyTo(null);
        setIsReplyAll(false);
        setView('compose');
    };

    const handleReply = (email) => {
        setReplyTo(email);
        setIsReplyAll(false);
        setView('reply');
    };

    const handleReplyAll = (email) => {
        setReplyTo(email);
        setIsReplyAll(true);
        setView('reply-all');
    };

    const handleBack = () => {
        setView('list');
        setSelectedEmail(null);
        setReplyTo(null);
        setIsReplyAll(false);
    };

    const handleSent = () => {
        setView('list');
        setSelectedEmail(null);
        setReplyTo(null);
        setIsReplyAll(false);
    };

    const handleAutomationDashboard = () => {
        const currentTab = tabs.find(tab => tab.id === activeTab);
        if (currentTab && currentTab.dashboardUrl) {
            window.open(currentTab.dashboardUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const currentTabConfig = tabs.find(tab => tab.id === activeTab);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="h-full flex flex-col"
        >
            {/* Tabs Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between px-6 py-4">
                    <div className="flex space-x-1">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    setView('list');
                                    setSelectedEmail(null);
                                    setReplyTo(null);
                                }}
                                className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${activeTab === tab.id
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Current Tab Email Info */}
                <div className="px-6 pb-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">Connected Account:</p>
                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-300">{currentTabConfig.email}</p>
                    </div>
                </div>
            </div>

            {/* Mailbox Content */}
            <div className="flex-1">
                {view === 'list' && (
                    <MailboxList
                        onEmailSelect={handleEmailSelect}
                        onCompose={handleCompose}
                        onFolderChange={setCurrentFolder}
                        emailAccount={currentTabConfig.email}
                        onAutomationDashboard={handleAutomationDashboard}
                    />
                )}

                {view === 'view' && selectedEmail && (
                    <EmailView
                        email={selectedEmail}
                        onBack={handleBack}
                        onReply={handleReply}
                        onReplyAll={handleReplyAll}
                        folder={currentFolder}
                    />
                )}

                {(view === 'compose' || view === 'reply' || view === 'reply-all') && (
                    <EmailCompose
                        replyTo={replyTo}
                        isReplyAll={isReplyAll}
                        voiceData={voiceComposeData}
                        onBack={handleBack}
                        onSent={handleSent}
                    />
                )}
            </div>
        </motion.div>
    );
};

export default Mailbox;
